import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TablePagination,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  styled,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../contexts/AuthContext';
import { mainApi } from '../../config/apiClient';
import AddPaymentIntegrationModal from './AddPaymentIntegrationModal';
import EditPaymentIntegrationModal from './EditPaymentIntegrationModal';
import BranchSearchFilterBar, { UNASSIGNED_BRANCH_ID } from '../common/BranchSearchFilterBar';
import GuideButton from '../common/guide/GuideButton';

interface PaymentIntegrationListItem {
  id: number;
  companyId: string;
  branchId?: string | null;
  provider: string;
  isEnabled: boolean;
  isTestMode: boolean;
  is3DEnabled: boolean;
  merchantId?: string | null;
  merchantKey?: string | null;
  merchantSalt?: string | null;
  clientCode?: string | null;
  clientUsername?: string | null;
  clientPassword?: string | null;
  guid?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const PROVIDER_COLORS: Record<string, string> = {
  paytr: '#0ea5e9',
  parampos: '#7c3aed',
  tacreocard: '#ea580c',
  moka: '#16a34a',
};

const providerLabel = (p: string) => {
  switch (p) {
    case 'paytr':
      return 'PayTR';
    case 'parampos':
      return 'ParamPos';
    case 'tacreocard':
      return 'Tacreo Kart';
    case 'moka':
      return 'Moka United';
    default:
      return p;
  }
};

const PaymentIntegrationsSection: React.FC = () => {
  const { branches } = useAuth();
  const isMobile = useMediaQuery('(max-width: 1499px)');

  const [items, setItems] = useState<PaymentIntegrationListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Şube + arama filtresi. Liste sunucu tarafında sayfalandığı için filtreler de
  // API'ye gönderilir; istemcide süzmek yalnızca açık sayfayı filtreler ve diğer
  // sayfalardaki kayıtları görünmez yapardı.
  const [filterBranchId, setFilterBranchId] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');

  // Her tuş vuruşunda istek atmamak için arama terimi geciktirilir.
  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(filterSearch.trim()), 400);
    return () => clearTimeout(timer);
  }, [filterSearch]);

  const isFilterActive = Boolean(filterBranchId) || appliedSearch.length > 0;

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<PaymentIntegrationListItem | null>(null);

  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [editId, setEditId] = useState<number | null>(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [toDelete, setToDelete] = useState<PaymentIntegrationListItem | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const branchNameById = useMemo(() => {
    const m = new Map<string, string>();
    branches.forEach((b) => m.set(b.id, b.name));
    return m;
  }, [branches]);

  const getBranchLabel = useCallback(
    (branchId: string | null | undefined): string => {
      if (!branchId) return 'Şirket Geneli';
      return branchNameById.get(branchId) || branchId;
    },
    [branchNameById]
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        pageSize: String(pageSize),
      });
      if (filterBranchId === UNASSIGNED_BRANCH_ID) {
        params.set('companyWideOnly', 'true');
      } else if (filterBranchId) {
        params.set('branchId', filterBranchId);
      }
      if (appliedSearch) {
        params.set('search', appliedSearch);
      }
      const response = await mainApi.get(
        `/api/paymentproviderintegration/list?${params.toString()}`
      );
      const result = response.data;
      if (result.Success === false || result.success === false) {
        throw new Error(result.Message || result.message || 'Liste yüklenemedi.');
      }
      const raw = result.Data || result.data || [];
      const mapped: PaymentIntegrationListItem[] = raw.map((x: any) => ({
        id: x.Id ?? x.id,
        companyId: x.CompanyId ?? x.companyId,
        branchId: x.BranchId ?? x.branchId ?? null,
        provider: (x.Provider ?? x.provider ?? '').toLowerCase(),
        isEnabled: !!(x.IsEnabled ?? x.isEnabled),
        isTestMode: !!(x.IsTestMode ?? x.isTestMode),
        is3DEnabled: !!(x.Is3DEnabled ?? x.is3DEnabled),
        merchantId: x.MerchantId ?? x.merchantId ?? null,
        merchantKey: x.MerchantKey ?? x.merchantKey ?? null,
        merchantSalt: x.MerchantSalt ?? x.merchantSalt ?? null,
        clientCode: x.ClientCode ?? x.clientCode ?? null,
        clientUsername: x.ClientUsername ?? x.clientUsername ?? null,
        clientPassword: x.ClientPassword ?? x.clientPassword ?? null,
        guid: x.Guid ?? x.guid ?? null,
        createdAt: x.CreatedAt ?? x.createdAt,
        updatedAt: x.UpdatedAt ?? x.updatedAt ?? null,
      }));
      setItems(mapped);
      setTotalCount(result.TotalCount ?? result.totalCount ?? mapped.length);
    } catch (err: any) {
      setError(
        err?.response?.data?.Message ||
          err?.response?.data?.message ||
          err?.message ||
          'Liste yüklenirken bir hata oluştu.'
      );
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterBranchId, appliedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: PaymentIntegrationListItem) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedItem(null);
  };

  const handleEdit = (item: PaymentIntegrationListItem) => {
    setEditId(item.id);
    setIsEditOpen(true);
  };

  const handleDelete = (item: PaymentIntegrationListItem) => {
    setToDelete(item);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const response = await mainApi.post('/api/paymentproviderintegration/delete', {
        Id: toDelete.id,
      });
      const result = response.data;
      if (result.Success === false || result.success === false) {
        throw new Error(result.Message || result.message || 'Silme işlemi başarısız.');
      }
      setSnackbar({
        open: true,
        message: `${providerLabel(toDelete.provider)} entegrasyonu silindi.`,
        severity: 'success',
      });
      setIsDeleteOpen(false);
      setToDelete(null);
      fetchList();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.Message ||
          err?.response?.data?.message ||
          err?.message ||
          'Silme işlemi sırasında bir hata oluştu.',
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const onSaved = () => {
    setSnackbar({ open: true, message: 'Kayıt başarıyla kaydedildi.', severity: 'success' });
    fetchList();
  };

  const renderRow = (item: PaymentIntegrationListItem) => {
    const color = PROVIDER_COLORS[item.provider] || '#1976d2';
    return (
      <TableRow key={item.id} hover>
        <TableCell>
          <Chip
            label={providerLabel(item.provider)}
            sx={{ bgcolor: color, color: 'white', fontWeight: 'bold' }}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">{getBranchLabel(item.branchId)}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{item.merchantId || '-'}</Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={item.isEnabled ? 'Aktif' : 'Pasif'}
              color={item.isEnabled ? 'success' : 'default'}
              size="small"
            />
            {item.isTestMode && <Chip label="Test" color="warning" size="small" />}
            {item.is3DEnabled && <Chip label="3D" color="primary" size="small" variant="outlined" />}
          </Box>
        </TableCell>
        <TableCell align="center" sx={{ p: '6px' }}>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, item)}>
            <MoreVertIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  const renderMobileCard = (item: PaymentIntegrationListItem) => {
    const color = PROVIDER_COLORS[item.provider] || '#1976d2';
    return (
      <Grid item xs={12} sm={6} key={item.id}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderLeft: 4,
            borderLeftColor: color,
            '&:hover': { boxShadow: 3 },
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={providerLabel(item.provider)}
                  sx={{ bgcolor: color, color: 'white', fontWeight: 'bold' }}
                  size="small"
                />
                <Chip
                  label={item.isEnabled ? 'Aktif' : 'Pasif'}
                  color={item.isEnabled ? 'success' : 'default'}
                  size="small"
                />
                {item.isTestMode && <Chip label="Test" color="warning" size="small" />}
              </Box>
              <IconButton size="small" onClick={(e) => handleMenuOpen(e, item)}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Şube:</strong> {getBranchLabel(item.branchId)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Merchant ID:</strong> {item.merchantId || '-'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      <HeaderContainer>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setIsAddOpen(true)}
        >
          Ödeme Entegrasyonu Ekle
        </Button>
      </HeaderContainer>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PaymentIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" fontWeight="bold">
          Ödeme Sağlayıcı Entegrasyonları
        </Typography>
        <Chip label={totalCount} size="small" color="primary" sx={{ ml: 1 }} />
        <GuideButton guideKey="integrations.payments" size="small" />
      </Box>

      {(totalCount > 0 || isFilterActive) && (
        <BranchSearchFilterBar
          branches={branches}
          branchId={filterBranchId}
          onBranchChange={(id) => {
            // Filtre değişince ilk sayfaya dön; yoksa 3. sayfada kalıp
            // sonuç yok gibi görünebilir.
            setFilterBranchId(id);
            setPage(0);
          }}
          search={filterSearch}
          onSearchChange={(value) => {
            setFilterSearch(value);
            setPage(0);
          }}
          searchLabel="Sağlayıcı / Merchant ID"
          unassignedLabel="Şirket Geneli"
        />
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center', mb: 3, bgcolor: 'grey.50' }}>
          <Typography color="text.secondary">
            {isFilterActive
              ? 'Filtreye uyan ödeme entegrasyonu bulunamadı.'
              : 'Henüz ödeme entegrasyonu tanımlanmamış. Sağ üstteki butondan PayTR bilgilerinizi ekleyebilirsiniz.'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {items.map(renderMobileCard)}
          </Grid>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Sayfa başına"
          />
        </>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Sağlayıcı
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Şube
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Merchant ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Durum
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: 'primary.main',
                      color: 'white',
                      textAlign: 'center',
                      width: 80,
                    }}
                  >
                    İşlem
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{items.map(renderRow)}</TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Sayfa başına"
          />
        </Paper>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            if (selectedItem) handleEdit(selectedItem);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="Düzenle" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (selectedItem) handleDelete(selectedItem);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Sil" />
        </MenuItem>
      </Menu>

      <AddPaymentIntegrationModal
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSaved={onSaved}
      />

      <EditPaymentIntegrationModal
        open={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setEditId(null);
        }}
        onSaved={onSaved}
        integrationId={editId}
      />

      <Dialog open={isDeleteOpen} onClose={() => setIsDeleteOpen(false)}>
        <DialogTitle>Ödeme Entegrasyonunu Sil</DialogTitle>
        <DialogContent>
          <Typography>
            {toDelete
              ? `${providerLabel(toDelete.provider)} entegrasyonunu silmek istediğinize emin misiniz? Bu işlem geri alınabilir (soft delete).`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteOpen(false)} disabled={deleting}>
            İptal
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentIntegrationsSection;
